import { API_BASE_URL } from './constants'
import { handOffToDeployment } from './handoff'
import { normalizeServerMessage, resolveApiErrorMessage } from '@/i18n/api-errors'

interface ApiErrorBody {
  message?: string | string[]
  errorCode?: string
  params?: Record<string, unknown>
}

/**
 * API 에러. HTTP status 를 보존하여 호출부가 메시지 문자열이 아닌 status 로 분기할 수 있게 한다.
 * message 는 throw 시점에 현재 UI 언어로 해석되므로(에러 코드 → 번역), 기존
 * `toast(error.message)` 호출부는 수정 없이 현지화된 메시지를 표시한다.
 */
export class ApiError extends Error {
  readonly status: number
  /** 백엔드의 기계 판독용 에러 코드 (예: 'IMAGE_SAFETY') */
  readonly code?: string
  /** 에러 메시지 보간 파라미터 (예: { max: 4 }) */
  readonly params?: Record<string, unknown>
  /** 백엔드 원문(한국어) 메시지 - 디버깅/설명용 */
  readonly serverMessage?: string

  constructor(status: number, body?: ApiErrorBody) {
    const serverMessage = normalizeServerMessage(body?.message)
    super(
      resolveApiErrorMessage({
        status,
        code: body?.errorCode,
        params: body?.params,
        serverMessage,
      })
    )
    this.name = 'ApiError'
    this.status = status
    this.code = body?.errorCode
    this.params = body?.params
    this.serverMessage = serverMessage
  }
}

/**
 * API 응답에서 /api/로 시작하는 모든 URL을 현재 환경의 API_BASE_URL로 변환
 * 재귀적으로 객체/배열 내부까지 처리
 */
export function transformApiUrls(data: unknown): unknown {
  if (typeof data === 'string') {
    if (data.startsWith('/api/')) {
      return data.replace(/^\/api/, API_BASE_URL)
    }
    return data
  }
  if (Array.isArray(data)) {
    return data.map(transformApiUrls)
  }
  if (data && typeof data === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(data)) {
      result[key] = transformApiUrls(value)
    }
    return result
  }
  return data
}

/**
 * 백엔드가 cross-deployment 경계(예: gov 세션이 wrks 배포에 도달)에서 반환하는
 * `{ redirect }` 응답인지 판별. infra split 미적용 시 백엔드는 이 응답을 내지
 * 않으므로 inert하다.
 */
export function isRedirectResponse(data: unknown): data is { redirect: string } {
  return (
    !!data &&
    typeof data === 'object' &&
    typeof (data as { redirect?: unknown }).redirect === 'string'
  )
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const isFormDataBody = typeof FormData !== 'undefined' && options.body instanceof FormData
    const headers: HeadersInit = isFormDataBody
      ? { ...options.headers }
      : {
          'Content-Type': 'application/json',
          ...options.headers,
        }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // 쿠키 기반 인증
    })

    if (!response.ok) {
      const body = await response.json().catch(() => undefined)
      throw new ApiError(response.status, body)
    }

    if (response.status === 204) {
      return null as T
    }

    const json = await response.json()
    if (isRedirectResponse(json)) {
      // 잘못된 배포에 도달한 세션 → 올바른 도메인으로 이동. navigation 중에는
      // 호출부가 잘못된 데이터로 렌더링하지 않도록 resolve하지 않는다.
      return handOffToDeployment(json.redirect)
    }
    return transformApiUrls(json) as T
  }

  get<T>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  post<T>(endpoint: string, data?: unknown) {
    const isFormDataBody = typeof FormData !== 'undefined' && data instanceof FormData
    return this.request<T>(endpoint, {
      method: 'POST',
      body: isFormDataBody ? data : (data ? JSON.stringify(data) : undefined),
    })
  }

  patch<T>(endpoint: string, data?: unknown) {
    const isFormDataBody = typeof FormData !== 'undefined' && data instanceof FormData
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: isFormDataBody ? data : (data ? JSON.stringify(data) : undefined),
    })
  }

  put<T>(endpoint: string, data?: unknown) {
    const isFormDataBody = typeof FormData !== 'undefined' && data instanceof FormData
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: isFormDataBody ? data : (data ? JSON.stringify(data) : undefined),
    })
  }

  delete<T>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }
}

export const api = new ApiClient(API_BASE_URL)
